import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { useRestaurantStore } from '@store/restaurantStore';
import { useAuthStore } from '@store/authStore';
import { useSocket } from '@/hooks/useSocket';
import { useNewOrderSound } from '@/hooks/useNewOrderSound';
import { useRestaurantNewOrderNotifications } from '@/hooks/useRestaurantNewOrderNotifications';
import { mapBackendOrderToFrontendOrder } from '@services/orderService';
import type { OrderFilters } from '@services/restaurantOrderService';
import {
  OrderDetailModal,
  RestaurantNewOrderNotificationStack,
} from '@organisms/index';
import type { RejectionReason } from '@organisms/RejectOrderModal';
import type { OrderStatus } from '@/types';
import SettingsTab from './restaurant/SettingsTab';
import OrdersTab from './restaurant/OrdersTab';
import MenuTab from './restaurant/MenuTab';
import DashboardTab from './restaurant/DashboardTab';
import AnalyticsTab from './restaurant/AnalyticsTab';
import FinanceTab from './restaurant/FinanceTab';
import PromotionsTab from './restaurant/PromotionsTab';
import DeliveryZonesTab from './restaurant/DeliveryZonesTab';
import CourierSettingsTab from './restaurant/CourierSettingsTab';
import DocumentsTab from './restaurant/DocumentsTab';
import { LegalConsentModal } from '@organisms/LegalConsentModal';
import { complianceService, type LegalStatusResponse } from '@services/complianceService';
import { DELIVERY_PROVIDER_LABELS } from '@/constants/deliveryProvider';
import { cn } from '@/utils/cn';

type RestaurantTab =
  | 'dashboard'
  | 'orders'
  | 'menu'
  | 'finance'
  | 'analytics'
  | 'promotions'
  | 'zones'
  | 'courier'
  | 'settings'
  | 'documents';

const VALID_TABS: RestaurantTab[] = [
  'dashboard',
  'analytics',
  'orders',
  'menu',
  'finance',
  'promotions',
  'zones',
  'courier',
  'settings',
  'documents',
];

const NAV_GROUPS = [
  {
    label: 'Genel',
    items: [
      { id: 'dashboard' as RestaurantTab, icon: '📊', label: 'Dashboard' },
      { id: 'analytics' as RestaurantTab, icon: '📈', label: 'Analitik' },
    ],
  },
  {
    label: 'İşlemler',
    items: [
      { id: 'orders' as RestaurantTab, icon: '📦', label: 'Siparişler' },
      { id: 'menu' as RestaurantTab, icon: '🍽️', label: 'Menü' },
    ],
  },
  {
    label: 'İş',
    items: [
      { id: 'finance' as RestaurantTab, icon: '💳', label: 'Finans' },
      { id: 'promotions' as RestaurantTab, icon: '🎁', label: 'Kampanyalar' },
    ],
  },
  {
    label: 'Yapılandırma',
    items: [
      { id: 'zones' as RestaurantTab, icon: '🗺️', label: 'Teslimat Bölgeleri' },
      { id: 'courier' as RestaurantTab, icon: '🛵', label: 'Kurye Ayarları' },
      { id: 'documents' as RestaurantTab, icon: '📄', label: 'Resmi Belgeler' },
      { id: 'settings' as RestaurantTab, icon: '⚙️', label: 'Ayarlar' },
    ],
  },
];

const PAGE_META: Record<RestaurantTab, { title: string; subtitle: string }> = {
  dashboard: { title: 'Dashboard', subtitle: 'Restoran performans özeti' },
  analytics: { title: 'Analitik', subtitle: 'Satış ve müşteri metrikleri' },
  orders: { title: 'Siparişler', subtitle: 'Aktif ve geçmiş siparişler' },
  menu: { title: 'Menü', subtitle: 'Ürün ve kategori yönetimi' },
  finance: { title: 'Finans', subtitle: 'Gelirler ve ödemeler' },
  promotions: { title: 'Kampanyalar', subtitle: 'İndirim ve promosyon yönetimi' },
  zones: { title: 'Teslimat Bölgeleri', subtitle: 'Teslimat bölgesi tanımları' },
  courier: { title: 'Kurye Ayarları', subtitle: 'Aktif kurye modeli ve teslimat yönlendirmesi' },
  documents: { title: 'Resmi Belgeler', subtitle: 'Resmi belgeler, başvuru formu ve KVKK onayı' },
  settings: { title: 'Ayarlar', subtitle: 'Restoran bilgileri ve çalışma saatleri' },
};

function parseTabParam(tab: string | null): RestaurantTab {
  if (tab && VALID_TABS.includes(tab as RestaurantTab)) {
    return tab as RestaurantTab;
  }
  return 'dashboard';
}

function orderFiltersForRealtimeSync(): OrderFilters {
  const last = useRestaurantStore.getState().lastOrderListFilters;
  if (!last) return {};
  const { status: _status, ...rest } = last;
  return rest;
}

const RestaurantPanelPage = () => {
  const {
    restaurant,
    loadRestaurant,
    loadOrders,
    loadStats,
    upsertOrderFromRealtime,
    updateOrderStatus,
    rejectOrder,
  } = useRestaurantStore();
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseTabParam(searchParams.get('tab'));
  const selectedOrderId = searchParams.get('orderId');
  const { socket, isConnected } = useSocket();
  const { playSound } = useNewOrderSound();
  const { notifications, enqueueFromOrder, dismiss } = useRestaurantNewOrderNotifications();
  const [newOrderIds, setNewOrderIds] = useState<ReadonlySet<string>>(new Set());
  const [legalStatus, setLegalStatus] = useState<LegalStatusResponse | null>(null);
  const [isCheckingLegalStatus, setIsCheckingLegalStatus] = useState(true);
  const newOrderIdsRef = useRef<Set<string>>(new Set());
  const enqueueFromOrderRef = useRef(enqueueFromOrder);
  const playSoundRef = useRef(playSound);

  useEffect(() => {
    enqueueFromOrderRef.current = enqueueFromOrder;
  }, [enqueueFromOrder]);

  useEffect(() => {
    playSoundRef.current = playSound;
  }, [playSound]);

  const openOrderDetail = useCallback(
    (orderId: string) => {
      setSearchParams({ tab: 'orders', orderId }, { replace: true });
      dismiss(orderId);
      if (newOrderIdsRef.current.has(orderId)) {
        newOrderIdsRef.current.delete(orderId);
        setNewOrderIds(new Set(newOrderIdsRef.current));
      }
    },
    [dismiss, setSearchParams],
  );

  const closeOrderDetail = useCallback(() => {
    setSearchParams({ tab: 'orders' }, { replace: true });
  }, [setSearchParams]);

  const handleNotificationSelect = useCallback(
    (orderId: string) => {
      openOrderDetail(orderId);
    },
    [openOrderDetail],
  );

  const handleOrderStatusChange = useCallback(
    async (orderId: string, newStatus: OrderStatus) => {
      await updateOrderStatus(orderId, newStatus);
      closeOrderDetail();
    },
    [closeOrderDetail, updateOrderStatus],
  );

  const handleOrderReject = useCallback(
    async (orderId: string, reason: RejectionReason, message?: string) => {
      await rejectOrder(orderId, reason, message);
      closeOrderDetail();
    },
    [closeOrderDetail, rejectOrder],
  );

  const handleOrderDetailRefresh = useCallback(() => {
    void loadOrders(orderFiltersForRealtimeSync(), {
      persistListFilters: false,
      skipLoadingIndicator: true,
    });
  }, [loadOrders]);

  useEffect(() => {
    if (!restaurant) {
      loadRestaurant();
    }
  }, [restaurant, loadRestaurant]);

  useEffect(() => {
    if (user?.role !== 'RESTAURANT' && user?.role !== 'ADMIN') {
      setIsCheckingLegalStatus(false);
      return;
    }
    setIsCheckingLegalStatus(true);
    complianceService
      .getLegalStatus()
      .then(setLegalStatus)
      .catch(() => setLegalStatus(null))
      .finally(() => setIsCheckingLegalStatus(false));
  }, [user?.role]);

  const handleLegalAccepted = useCallback(() => {
    complianceService.getLegalStatus().then(setLegalStatus).catch(() => setLegalStatus(null));
  }, []);

  useEffect(() => {
    if (!searchParams.get('tab')) {
      const nextParams: Record<string, string> = { tab: 'dashboard' };
      if (selectedOrderId) {
        nextParams.orderId = selectedOrderId;
      }
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, selectedOrderId, setSearchParams]);

  useEffect(() => {
    if (!socket) return;
    if (user?.role !== 'RESTAURANT' && user?.role !== 'ADMIN') return;

    const handleNewOrder = (payload: { order?: unknown }) => {
      try {
        if (payload?.order && typeof payload.order === 'object') {
          const mapped = mapBackendOrderToFrontendOrder(payload.order as any);
          upsertOrderFromRealtime(mapped);
          const isNew = enqueueFromOrderRef.current(mapped);
          if (isNew) {
            void playSoundRef.current();
            newOrderIdsRef.current = new Set([...newOrderIdsRef.current, mapped.id]);
            setNewOrderIds(new Set(newOrderIdsRef.current));
          }
        } else {
          void loadOrders(orderFiltersForRealtimeSync(), {
            persistListFilters: false,
            skipLoadingIndicator: true,
          });
        }
      } catch {
        void loadOrders(orderFiltersForRealtimeSync(), {
          persistListFilters: false,
          skipLoadingIndicator: true,
        });
      }
      void loadStats();
    };

    const handleOrderStatusChanged = (payload: unknown) => {
      if (payload && typeof payload === 'object') {
        try {
          upsertOrderFromRealtime(mapBackendOrderToFrontendOrder(payload as any));
          return;
        } catch {
          // fall through to HTTP refetch if mapping fails
        }
      }
      void loadOrders(orderFiltersForRealtimeSync(), {
        persistListFilters: false,
        skipLoadingIndicator: true,
      });
    };

    socket.on('NEW_ORDER', handleNewOrder);
    socket.on('ORDER_STATUS_CHANGED', handleOrderStatusChanged);

    return () => {
      socket.off('NEW_ORDER', handleNewOrder);
      socket.off('ORDER_STATUS_CHANGED', handleOrderStatusChanged);
    };
  }, [socket, user?.role, loadOrders, loadStats, upsertOrderFromRealtime]);

  const setActiveTab = (id: RestaurantTab) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const meta = PAGE_META[activeTab];

  return (
    <MainLayout>
      <RestaurantNewOrderNotificationStack
        notifications={notifications}
        onDismiss={dismiss}
        onSelect={handleNotificationSelect}
      />

      {selectedOrderId && (
        <OrderDetailModal
          orderId={selectedOrderId}
          onClose={closeOrderDetail}
          onStatusChange={handleOrderStatusChange}
          onReject={handleOrderReject}
          onCourierAssigned={handleOrderDetailRefresh}
        />
      )}

      <div className="flex -mx-4 -my-8 min-h-[calc(100vh-64px)]">
        <aside className="w-56 shrink-0 bg-white border-r border-gray-100 flex flex-col shadow-sm">
          <div className="px-5 pt-7 pb-5 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-[#E63946] flex items-center justify-center shadow-sm">
                <span className="text-white text-sm">🍽️</span>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-gray-900 leading-tight truncate">
                  {restaurant?.name || 'Restoran Paneli'}
                </p>
                <p className="text-[10px] text-gray-400 leading-tight">Yönetim paneli</p>
                {restaurant?.deliveryProviderType && (
                  <p className="text-[10px] font-medium text-[#E63946] leading-tight mt-1 truncate">
                    {DELIVERY_PROVIDER_LABELS[restaurant.deliveryProviderType]}
                  </p>
                )}
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto py-3 px-3">
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className="mb-5">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 px-2 mb-1.5">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setActiveTab(item.id)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-sm font-medium transition-all duration-150 cursor-pointer mb-0.5',
                        isActive
                          ? 'bg-red-50 text-[#E63946] border-l-[3px] border-[#E63946] pl-[7px]'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l-[3px] border-transparent'
                      )}
                    >
                      <span className="text-base leading-none">{item.icon}</span>
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          <div className="px-4 py-4 border-t border-gray-100 space-y-2">
            <div className="flex items-center justify-center gap-2">
              <div
                className={cn(
                  'w-2 h-2 rounded-full',
                  isConnected ? 'bg-green-500' : 'bg-red-500',
                )}
              />
              <span className="text-[10px] text-gray-500">
                {isConnected ? 'Canlı bildirim bağlı' : 'Bildirim bağlantısı yok'}
              </span>
            </div>
            <p className="text-[10px] text-gray-400 text-center">Lezzet Yolda</p>
          </div>
        </aside>

        <div className="flex-1 min-w-0 py-8 px-8 bg-[#F9FAFB]">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">{meta.title}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{meta.subtitle}</p>
          </div>

          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'orders' && (
            <OrdersTab
              socketConnected={isConnected}
              newOrderIds={newOrderIds}
              onSelectOrderId={openOrderDetail}
            />
          )}
          {activeTab === 'menu' && <MenuTab />}
          {activeTab === 'finance' && <FinanceTab />}
          {activeTab === 'analytics' && <AnalyticsTab />}
          {activeTab === 'promotions' && <PromotionsTab />}
          {activeTab === 'zones' && <DeliveryZonesTab />}
          {activeTab === 'courier' && <CourierSettingsTab />}
          {activeTab === 'documents' && <DocumentsTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </div>
      </div>

      {!isCheckingLegalStatus &&
        legalStatus?.requiresAcceptance &&
        legalStatus.document && (
          <LegalConsentModal legalStatus={legalStatus} onAccepted={handleLegalAccepted} />
        )}
    </MainLayout>
  );
};

export default RestaurantPanelPage;
